import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SeoContent from '../components/SeoContent';
import PageSEO from '../components/PageSEO';
import { countSyllables } from '../utils/syllables';

// Map a Flesch Reading Ease score (0–100, higher = easier) to a plain-English label.
function readingEaseLabel(score) {
  if (score >= 90) return { label: 'Very Easy',        grade: '5th grade',        color: '#16a34a' };
  if (score >= 80) return { label: 'Easy',             grade: '6th grade',        color: '#22c55e' };
  if (score >= 70) return { label: 'Fairly Easy',      grade: '7th grade',        color: '#65a30d' };
  if (score >= 60) return { label: 'Plain English',    grade: '8th–9th grade',    color: '#ca8a04' };
  if (score >= 50) return { label: 'Fairly Difficult', grade: '10th–12th grade',  color: '#ea580c' };
  if (score >= 30) return { label: 'Difficult',        grade: 'College',          color: '#dc2626' };
  return                    { label: 'Very Difficult',  grade: 'College graduate', color: '#b91c1c' };
}

// Text-case transforms for the converter buttons.
const CASE_TRANSFORMS = {
  UPPERCASE:     (t) => t.toUpperCase(),
  lowercase:     (t) => t.toLowerCase(),
  'Title Case':  (t) => t.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase()),
  'Sentence case': (t) => t.toLowerCase().replace(/(^\s*[a-z])|([.!?]\s*[a-z])/g, (c) => c.toUpperCase()),
};

const STOP_WORDS = new Set(['the','a','an','and','or','but','in','on','at','to','for',
  'of','with','by','from','is','are','was','were','be','been','being','have','has',
  'had','do','does','did','will','would','could','should','may','might','shall','can',
  'i','you','he','she','it','we','they','me','him','her','us','them','my','your',
  'his','its','our','their','this','that','these','those','not','no','so','as','if',
  'up','out','about','into','than','then','when','where','who','which','what','how']);

// Popular social-media character limits, smallest first so the tightest
// constraints (the ones you're most likely to blow past) sit at the top.
const PLATFORMS = [
  { name: 'SMS text',            emoji: '💬', limit: 160,   color: '#22c55e' },
  { name: 'X / Twitter post',    emoji: '🐦', limit: 280,   color: '#1d9bf0' },
  { name: 'Bluesky post',        emoji: '🦋', limit: 300,   color: '#0085ff' },
  { name: 'Threads post',        emoji: '🧵', limit: 500,   color: '#8b5cf6' },
  { name: 'Instagram caption',   emoji: '📸', limit: 2200,  color: '#e1306c' },
  { name: 'TikTok caption',      emoji: '🎵', limit: 2200,  color: '#ec4899' },
  { name: 'LinkedIn post',       emoji: '💼', limit: 3000,  color: '#0a66c2' },
  { name: 'Facebook post',       emoji: '👍', limit: 63206, color: '#1877f2' },
];

function analyze(text) {
  if (!text.trim()) return null;

  const words = text.trim().match(/\b[a-zA-Z'-]+\b/g) || [];
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);
  const charsWithSpaces = text.length;
  const charsNoSpaces = text.replace(/\s/g, '').length;

  const readingTime = Math.max(1, Math.round(words.length / 200));
  const speakingTime = Math.max(1, Math.round(words.length / 130));

  const uniqueWords = new Set(words.map(w => w.toLowerCase()));

  const longest = words.reduce((m, w) => w.length > m.length ? w : m, '');

  const freq = {};
  words.forEach(w => {
    const key = w.toLowerCase().replace(/['-]/g, '');
    if (key.length > 1 && !STOP_WORDS.has(key)) freq[key] = (freq[key] || 0) + 1;
  });
  const topWords = Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  const avgWordLen = words.length
    ? (words.reduce((s, w) => s + w.replace(/['-]/g,'').length, 0) / words.length).toFixed(1)
    : 0;

  // Readability (Flesch Reading Ease & Flesch–Kincaid grade level).
  const sentenceCount = Math.max(1, sentences.length);
  const totalSyllables = words.reduce((s, w) => s + countSyllables(w), 0);
  const wordsPerSentence = words.length / sentenceCount;
  const syllablesPerWord = words.length ? totalSyllables / words.length : 0;
  const readingEase = words.length
    ? Math.max(0, Math.min(100, 206.835 - 1.015 * wordsPerSentence - 84.6 * syllablesPerWord))
    : 0;
  const gradeLevel = words.length
    ? Math.max(0, 0.39 * wordsPerSentence + 11.8 * syllablesPerWord - 15.59)
    : 0;

  return {
    wordCount: words.length,
    charWithSpaces: charsWithSpaces,
    charNoSpaces: charsNoSpaces,
    sentences: sentences.length,
    paragraphs: paragraphs.length,
    uniqueWords: uniqueWords.size,
    readingTime,
    speakingTime,
    longest,
    avgWordLen,
    topWords,
    readingEase: Math.round(readingEase),
    gradeLevel: gradeLevel.toFixed(1),
    avgWordsPerSentence: wordsPerSentence.toFixed(1),
    avgSyllablesPerWord: syllablesPerWord.toFixed(2),
  };
}

const SAMPLE = `The quick brown fox jumps over the lazy dog. This classic sentence contains every letter of the English alphabet. Word counters are essential tools for writers, students, and professionals who need to meet specific length requirements for essays, articles, and reports.`;

export default function WordCounter() {
  const [text, setText] = useState('');
  const [stats, setStats] = useState(null);

  const handleChange = useCallback((e) => {
    const val = e.target.value;
    setText(val);
    setStats(analyze(val));
  }, []);

  function loadSample() {
    setText(SAMPLE);
    setStats(analyze(SAMPLE));
  }

  function clearAll() {
    setText('');
    setStats(null);
  }

  function applyCase(fn) {
    const next = fn(text);
    setText(next);
    setStats(analyze(next));
  }

  const STAT_CARDS = stats ? [
    { label: 'Words', value: stats.wordCount, icon: '📝', color: '#4f46e5' },
    { label: 'Characters', value: stats.charWithSpaces, icon: '🔤', color: '#0891b2' },
    { label: 'No Spaces', value: stats.charNoSpaces, icon: '✂️', color: '#7c3aed' },
    { label: 'Sentences', value: stats.sentences, icon: '📖', color: '#ea580c' },
    { label: 'Paragraphs', value: stats.paragraphs, icon: '📄', color: '#059669' },
    { label: 'Unique Words', value: stats.uniqueWords, icon: '🔑', color: '#0d9488' },
  ] : [];

  return (
    <div className="game-page">
      <PageSEO page="word-counter" />
      <div className="container" style={{ maxWidth: 800 }}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">📝</span>
          <h1>Word Counter</h1>
          <p>Count words, characters, sentences and paragraphs instantly. Get reading time, speaking time, and keyword frequency.</p>
        </div>

        <div className="solver-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="fw-bold">Paste or type your text below</label>
            <div>
              <button className="btn btn-sm me-2 fw-bold"
                style={{ borderRadius: 50, background: 'var(--border)', border: 'none', fontSize: '0.8rem' }}
                onClick={loadSample}>Load Sample</button>
              <button className="btn btn-sm fw-bold"
                style={{ borderRadius: 50, background: '#fee2e2', border: 'none', color: '#991b1b', fontSize: '0.8rem' }}
                onClick={clearAll}>Clear</button>
            </div>
          </div>
          <textarea
            className="form-control"
            rows={8}
            value={text}
            onChange={handleChange}
            placeholder="Start typing or paste your text here…"
            style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1rem', borderRadius: 12, lineHeight: 1.7 }}
            autoFocus
          />
          {/* Case converter */}
          <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
            <span className="text-muted fw-bold" style={{ fontSize: '0.8rem' }}>Convert case:</span>
            {Object.keys(CASE_TRANSFORMS).map(name => (
              <button
                key={name}
                type="button"
                className="btn btn-sm fw-bold"
                disabled={!text}
                style={{
                  borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe',
                  color: '#4f46e5', fontSize: '0.78rem', padding: '0.22rem 0.7rem',
                  fontFamily: 'Nunito, sans-serif', opacity: text ? 1 : 0.5,
                }}
                onClick={() => applyCase(CASE_TRANSFORMS[name])}
              >
                {name}
              </button>
            ))}
          </div>

          {/* Live word count bar */}
          <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.82rem', color: '#6b7280' }}>
            <span><strong style={{ color: '#4f46e5' }}>{stats?.wordCount ?? 0}</strong> words</span>
            <span><strong style={{ color: '#0891b2' }}>{stats?.charWithSpaces ?? 0}</strong> characters</span>
            <span><strong style={{ color: '#059669' }}>{stats?.sentences ?? 0}</strong> sentences</span>
          </div>
        </div>

        {stats && stats.wordCount > 0 && (
          <>
            {/* Main stats grid */}
            <div className="row g-3 mb-3">
              {STAT_CARDS.map(s => (
                <div key={s.label} className="col-6 col-md-4">
                  <div className="solver-card text-center py-3 mb-0">
                    <div style={{ fontSize: '1.4rem' }}>{s.icon}</div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 800, color: s.color, lineHeight: 1.2 }}>{s.value.toLocaleString()}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social media post fit meter */}
            <div className="solver-card mb-3" style={{
              background: 'linear-gradient(180deg, #fafbff 0%, #ffffff 60%)',
            }}>
              <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 8 }}>
                <h5 className="fw-bold mb-0">📱 Social Media Post Fit</h5>
                <span style={{
                  background: '#ecfeff', color: '#0e7490', fontWeight: 800,
                  fontSize: '0.72rem', padding: '0.2rem 0.6rem', borderRadius: 99,
                }}>
                  {stats.charWithSpaces.toLocaleString()} characters
                </span>
              </div>
              <p className="text-muted mb-3" style={{ fontSize: '0.82rem' }}>
                See at a glance which platforms your post fits — green fits, amber is close, red is over.
              </p>
              <div className="row g-2">
                {PLATFORMS.map(p => {
                  const used = stats.charWithSpaces;
                  const over = used > p.limit;
                  const pct = (used / p.limit) * 100;
                  const barPct = Math.min(100, pct);
                  const near = !over && pct >= 90;
                  const status = over
                    ? { label: 'Over', bg: '#fee2e2', fg: '#dc2626', icon: '⚠' }
                    : near
                      ? { label: 'Near limit', bg: '#fef3c7', fg: '#d97706', icon: '⚠' }
                      : { label: 'Fits', bg: '#dcfce7', fg: '#16a34a', icon: '✓' };
                  return (
                    <div key={p.name} className="col-12 col-md-6">
                      <div style={{
                        background: '#fff', border: '1px solid var(--border)',
                        borderLeft: `4px solid ${over ? '#dc2626' : p.color}`,
                        borderRadius: 12, padding: '0.7rem 0.85rem', height: '100%',
                      }}>
                        <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 8 }}>
                          <span className="d-flex align-items-center" style={{ gap: 8, minWidth: 0 }}>
                            <span style={{
                              width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                              background: `${p.color}1a`, display: 'inline-flex',
                              alignItems: 'center', justifyContent: 'center', fontSize: '1rem',
                            }}>{p.emoji}</span>
                            <span style={{ fontWeight: 700, fontSize: '0.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.name}
                            </span>
                          </span>
                          <span style={{
                            background: status.bg, color: status.fg, fontWeight: 800,
                            fontSize: '0.68rem', padding: '0.18rem 0.55rem', borderRadius: 99,
                            whiteSpace: 'nowrap', flexShrink: 0,
                          }}>
                            {status.icon} {status.label}
                          </span>
                        </div>
                        <div style={{ background: '#f1f5f9', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                          <div style={{
                            width: `${barPct}%`, height: '100%', borderRadius: 99,
                            background: over ? 'linear-gradient(90deg,#f87171,#dc2626)' : p.color,
                            transition: 'width 0.35s ease',
                          }} />
                        </div>
                        <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                          <span><strong style={{ color: '#475569' }}>{used.toLocaleString()}</strong> / {p.limit.toLocaleString()}</span>
                          <span style={{ fontWeight: 700, color: over ? '#dc2626' : (near ? '#d97706' : '#64748b') }}>
                            {over ? `${(used - p.limit).toLocaleString()} over` : `${(p.limit - used).toLocaleString()} left`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Readability */}
            {(() => {
              const r = readingEaseLabel(stats.readingEase);
              return (
                <div className="solver-card mb-3">
                  <h5 className="fw-bold mb-3">📊 Readability</h5>
                  <div className="row g-3 align-items-center">
                    <div className="col-md-7">
                      <div className="d-flex align-items-baseline flex-wrap gap-2 mb-2">
                        <span style={{ fontSize: '2.4rem', fontWeight: 800, color: r.color, lineHeight: 1 }}>{stats.readingEase}</span>
                        <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>/ 100</span>
                        <span style={{ background: `${r.color}1a`, color: r.color, fontWeight: 800, fontSize: '0.8rem', padding: '0.2rem 0.7rem', borderRadius: 99 }}>
                          {r.label}
                        </span>
                      </div>
                      <div style={{ position: 'relative', height: 10, borderRadius: 99, background: 'linear-gradient(90deg,#dc2626,#ea580c,#ca8a04,#22c55e,#16a34a)' }}>
                        <div style={{ position: 'absolute', top: -3, left: `calc(${stats.readingEase}% - 3px)`, width: 6, height: 16, background: '#1e293b', borderRadius: 3, border: '2px solid #fff' }} />
                      </div>
                      <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                        <span>Harder</span><span>Easier</span>
                      </div>
                      <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.82rem' }}>
                        Flesch Reading Ease — higher is easier to read. Your text reads at a <strong>{r.grade}</strong> level.
                      </p>
                    </div>
                    <div className="col-md-5">
                      {[
                        { label: 'Grade level', value: stats.gradeLevel, sub: 'U.S. school grade' },
                        { label: 'Words / sentence', value: stats.avgWordsPerSentence, sub: 'shorter is clearer' },
                        { label: 'Syllables / word', value: stats.avgSyllablesPerWord, sub: 'lower is simpler' },
                      ].map(m => (
                        <div key={m.label} className="d-flex align-items-center justify-content-between mb-2"
                             style={{ background: '#f8fafc', borderRadius: 10, padding: '0.5rem 0.85rem' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{m.label}</div>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{m.sub}</div>
                          </div>
                          <div style={{ fontWeight: 800, fontSize: '1.15rem', color: '#4f46e5' }}>{m.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Reading & speaking time + word info */}
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <div className="solver-card mb-0">
                  <h5 className="fw-bold mb-3">⏱ Time Estimates</h5>
                  {[
                    { label: 'Reading Time', value: `~${stats.readingTime} min`, sub: 'at 200 words/min', color: '#4f46e5' },
                    { label: 'Speaking Time', value: `~${stats.speakingTime} min`, sub: 'at 130 words/min', color: '#ea580c' },
                  ].map(item => (
                    <div key={item.label} className="d-flex align-items-center mb-2"
                         style={{ background: '#f8fafc', borderRadius: 10, padding: '0.6rem 1rem' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{item.label}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.sub}</div>
                      </div>
                      <div style={{ fontWeight: 800, fontSize: '1.2rem', color: item.color }}>{item.value}</div>
                    </div>
                  ))}
                  <div className="d-flex gap-3 mt-3 pt-2" style={{ borderTop: '1px solid var(--border)', fontSize: '0.85rem' }}>
                    <div><span className="text-muted">Avg word length:</span> <strong>{stats.avgWordLen}</strong></div>
                    <div><span className="text-muted">Longest word:</span> <strong style={{ color: '#7c3aed' }}>{stats.longest || '—'}</strong></div>
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="solver-card mb-0">
                  <h5 className="fw-bold mb-3">🔑 Top Keywords</h5>
                  {stats.topWords.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: '0.9rem' }}>No significant keywords found.</p>
                  ) : (
                    stats.topWords.map(([word, count], i) => {
                      const pct = Math.round((count / stats.wordCount) * 100);
                      return (
                        <div key={word} className="mb-2">
                          <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem' }}>
                            <span style={{ fontWeight: 700 }}>#{i + 1} {word}</span>
                            <span style={{ color: '#6b7280' }}>{count}× ({pct}%)</span>
                          </div>
                          <div style={{ background: '#f1f5f9', borderRadius: 99, height: 6, overflow: 'hidden' }}>
                            <div style={{
                              width: `${Math.min(100, pct * 5)}%`, height: '100%',
                              background: 'linear-gradient(90deg, #4f46e5, #7c3aed)', borderRadius: 99
                            }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        <SeoContent game="word-counter" />
      </div>
    </div>
  );
}
